/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Trophy, Users, Calendar, Upload, Camera, Fish, MapPin,
  Plus, Crown, Medal, Award, ChevronRight, Target, Gift,
  Lock, UserPlus, Copy, Check, Loader2, Flag, BarChart3, AlertTriangle,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CatchImage from '../components/CatchImage';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import SubmitCatchModal from '../components/SubmitCatchModal';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  base_species: 'Base species',
  variant_bonus: 'Carp variant',
  weight_bonus: 'Weight tiers',
  photo: 'Photo evidence',
  first_fish_of_week: 'First fish of week',
  night_catch: 'Night catches',
  new_lake: 'New lakes',
  back_to_back: 'Back-to-back',
  used_bundle: 'Bundles used',
  full_session: 'Full sessions',
};

const RankIcon = ({ rank }) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-[#6b7d6b]">{rank}</span>;
};

const LeaguePage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('community');
  const [communityLeague, setCommunityLeague] = useState(null);
  const [privateLeagues, setPrivateLeagues] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recentCatches, setRecentCatches] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [bundles, setBundles] = useState({});
  const [pointsConfig, setPointsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);

  // Modal states
  const [showSubmitCatch, setShowSubmitCatch] = useState(false);
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [showJoinLeague, setShowJoinLeague] = useState(false);
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  const [showStats, setShowStats] = useState(false);

  // Form states for create/join
  const [createLeagueForm, setCreateLeagueForm] = useState({ name: '', description: '', max_members: '' });
  const [inviteCode, setInviteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const refreshLeaderboard = useCallback(async (leagueId) => {
    try {
      const r = await axios.get(`${API}/leagues/${leagueId}/leaderboard`);
      setLeaderboard(r.data.leaderboard || []);
    } catch {}
  }, []);

  const refreshCatches = useCallback(async (leagueId) => {
    try {
      const r = await axios.get(`${API}/leagues/${leagueId}/catches?limit=10`);
      setRecentCatches(r.data || []);
    } catch {}
  }, []);

  const refreshMyStats = useCallback(async (leagueId) => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/leagues/${leagueId}/my-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyStats(r.data);
    } catch {}
  }, [token]);

  const fetchUserLeagues = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/leagues/user/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrivateLeagues((r.data || []).filter((l) => l.league_type === 'private'));
    } catch {}
  }, [token]);

  const fetchCommunity = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/leagues/community`);
      setCommunityLeague(r.data);
      if (token) {
        try {
          const lr = await axios.get(`${API}/leagues/${r.data.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setIsMember(lr.data.is_member);
        } catch {}
      }
      refreshLeaderboard(r.data.id);
      refreshCatches(r.data.id);
      refreshMyStats(r.data.id);
    } catch (e) {
      console.error('community fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [token, refreshLeaderboard, refreshCatches, refreshMyStats]);

  useEffect(() => {
    fetchCommunity();
    fetchUserLeagues();
    axios.get(`${API}/subscriptions/bait-bundles`).then((r) => setBundles(r.data || {})).catch(() => {});
    axios.get(`${API}/leagues/points-config`).then((r) => setPointsConfig(r.data)).catch(() => {});
  }, [fetchCommunity, fetchUserLeagues]);

  const handleJoinCommunityLeague = async () => {
    if (!token) {
      toast.error('Please log in to join the league');
      return;
    }
    try {
      await axios.post(`${API}/leagues/${communityLeague.id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsMember(true);
      toast.success('Welcome to The League!');
      fetchCommunity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    }
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!createLeagueForm.name) return toast.error('Enter a league name');
    setSubmitting(true);
    try {
      await axios.post(`${API}/leagues`, {
        ...createLeagueForm,
        league_type: 'private',
        max_members: createLeagueForm.max_members ? parseInt(createLeagueForm.max_members) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('League created! Share the invite code with friends.');
      setShowCreateLeague(false);
      setCreateLeagueForm({ name: '', description: '', max_members: '' });
      fetchUserLeagues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinPrivateLeague = async (e) => {
    e.preventDefault();
    if (!inviteCode) return toast.error('Enter an invite code');
    setSubmitting(true);
    try {
      const lr = await axios.get(`${API}/leagues?league_type=private`);
      const league = (lr.data || []).find((l) => l.invite_code === inviteCode.toUpperCase());
      if (!league) {
        toast.error('Invalid invite code');
        setSubmitting(false);
        return;
      }
      await axios.post(`${API}/leagues/${league.id}/join`, {
        league_id: league.id,
        invite_code: inviteCode.toUpperCase(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Joined ${league.name}!`);
      setShowJoinLeague(false);
      setInviteCode('');
      fetchUserLeagues();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const reportCatch = async (catchId) => {
    if (!token) return toast.error('Log in to report');
    if (!window.confirm('Report this catch as suspicious? Reports are anonymous. 3 reports auto-hides until admin review.')) return;
    try {
      const r = await axios.post(`${API}/leagues/catches/${catchId}/report`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data.auto_hidden) {
        toast.success('Reported. Catch auto-hidden pending admin review.');
      } else {
        toast.success(`Reported. Flag count: ${r.data.flags_count}/3`);
      }
      if (communityLeague) refreshCatches(communityLeague.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not report');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    toast.success('Invite code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#c8b273]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[#141714] to-[#0b0d0b] py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy className="w-10 h-10 text-[#c8b273]" />
              <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white">
                The League
              </h1>
            </div>
            <p className="text-[#8a948a] max-w-2xl mx-auto">
              Skill, consistency &amp; engagement — log catches, earn points, win a 48-hour fishing ticket. Photos required.
            </p>
            {isMember && myStats && (
              <div className="flex justify-center gap-3 mt-6 flex-wrap">
                <div className="px-4 py-2 bg-[#c8b273]/10 border border-[#c8b273]/30 rounded-lg">
                  <span className="text-[#6b7d6b] text-xs uppercase tracking-wider">Your rank</span>
                  <span className="ml-2 font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">
                    {myStats.rank ? `#${myStats.rank}` : '—'}
                  </span>
                </div>
                <div className="px-4 py-2 bg-[#c8b273]/10 border border-[#c8b273]/30 rounded-lg">
                  <span className="text-[#6b7d6b] text-xs uppercase tracking-wider">Points</span>
                  <span className="ml-2 font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">{myStats.total_points}</span>
                </div>
                <div className="px-4 py-2 bg-[#c8b273]/10 border border-[#c8b273]/30 rounded-lg">
                  <span className="text-[#6b7d6b] text-xs uppercase tracking-wider">Catches</span>
                  <span className="ml-2 font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">{myStats.catch_count}</span>
                </div>
                <Button variant="ghost" className="text-[#c8b273]" onClick={() => setShowStats(true)} data-testid="my-stats-btn">
                  <BarChart3 className="w-4 h-4 mr-1" /> My Stats
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-[#141714] mb-8">
            <TabsTrigger value="community" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black">
              <Trophy className="w-4 h-4 mr-2" /> Community League
            </TabsTrigger>
            <TabsTrigger value="private" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" /> Private Leagues
            </TabsTrigger>
          </TabsList>

          {/* Community */}
          <TabsContent value="community">
            {communityLeague && (
              <div className="space-y-8">
                <motion.div className="glass-card p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
                        {communityLeague.name}
                      </h2>
                      <p className="text-[#8a948a] mt-1">{communityLeague.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
                        <Link
                          to="/members"
                          className="flex items-center gap-1 text-[#c8b273] hover:underline"
                          data-testid="league-members-link"
                        >
                          <Users className="w-4 h-4" />
                          {communityLeague.member_count} members
                          <Search className="w-3 h-3 ml-1 opacity-60" />
                        </Link>
                        <span className="flex items-center gap-1 text-[#6b7d6b]">
                          <Calendar className="w-4 h-4" /> Ends {new Date(communityLeague.end_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {!isMember ? (
                        <Button className="btn-gold" onClick={handleJoinCommunityLeague} data-testid="join-community-league-btn">
                          <UserPlus className="w-4 h-4 mr-2" /> Join League
                        </Button>
                      ) : (
                        <Button className="btn-gold" onClick={() => setShowSubmitCatch(true)} data-testid="submit-catch-btn">
                          <Upload className="w-4 h-4 mr-2" /> Submit Catch
                        </Button>
                      )}
                      <Button variant="ghost" className="text-[#c8b273] text-xs" onClick={() => setShowPointsInfo(true)} data-testid="how-points-work-btn">
                        <Target className="w-3 h-3 mr-1" /> How Points Work
                      </Button>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-gradient-to-r from-[#c8b273]/20 to-transparent rounded-lg border border-[#c8b273]/30">
                    <div className="flex items-center gap-3">
                      <Gift className="w-8 h-8 text-[#c8b273]" />
                      <div>
                        <p className="text-xs text-[#c8b273] uppercase tracking-wider font-bold">League Prize</p>
                        <p className="text-white font-bold">{communityLeague.prize_description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Leaderboard */}
                  <motion.div className="glass-card p-6" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#c8b273]" /> Leaderboard
                    </h3>
                    {leaderboard.length === 0 ? (
                      <div className="text-center py-8 text-[#6b7d6b]">
                        <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No catches submitted yet.</p>
                        <p className="text-sm">Be the first to log a catch!</p>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="leaderboard-list">
                        {leaderboard.slice(0, 10).map((entry) => (
                          <Link
                            key={entry.user_id}
                            to={`/u/${entry.user_id}`}
                            className={`flex items-center gap-3 p-3 rounded-lg hover:border-[#c8b273] border border-transparent transition-colors ${
                              entry.rank <= 3 ? 'bg-[#c8b273]/10 border-[#c8b273]/30' : 'bg-[#141714]'
                            } ${entry.user_id === user?.id ? 'ring-2 ring-[#22c55e]' : ''}`}
                            data-testid={`leaderboard-row-${entry.rank}`}
                          >
                            <div className="w-8 flex justify-center"><RankIcon rank={entry.rank} /></div>
                            <div className="w-8 h-8 rounded-full bg-[#c8b273]/20 flex items-center justify-center">
                              <span className="text-sm font-bold text-[#c8b273]">{entry.username?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{entry.username}</p>
                              <p className="text-[#6b7d6b] text-xs">{entry.catch_count} catches • Best: {entry.biggest_fish}lb</p>
                            </div>
                            <div className="text-right">
                              <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#c8b273]">{entry.total_points}</p>
                              <p className="text-[#6b7d6b] text-xs">points</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>

                  {/* Recent catches with Report */}
                  <motion.div className="glass-card p-6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-4 flex items-center gap-2">
                      <Fish className="w-5 h-5 text-[#c8b273]" /> Recent Catches
                    </h3>
                    {recentCatches.length === 0 ? (
                      <div className="text-center py-8 text-[#6b7d6b]">
                        <Camera className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No catches yet this season.</p>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid="recent-catches-list">
                        {recentCatches.map((c) => (
                          <div key={c.id} className="flex gap-3 p-3 bg-[#141714] rounded-lg" data-testid={`recent-catch-${c.id}`}>
                            <CatchImage row={c} aspectRatio="square" rounded className="w-16 h-16 flex-shrink-0" testId={`league-recent-catch-image-${c.id}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                {c.user_id ? (
                                  <Link
                                    to={`/u/${c.user_id}`}
                                    className="text-white font-medium truncate hover:text-[#c8b273]"
                                  >
                                    {c.username}
                                  </Link>
                                ) : (
                                  <p className="text-white font-medium truncate">{c.username}</p>
                                )}
                                <span className="text-[#22c55e] text-sm font-bold whitespace-nowrap">+{c.points} pts</span>
                              </div>
                              <p className="text-[#c8b273] font-bold">{c.weight}lb {c.fish_type}</p>
                              <div className="flex items-center justify-between gap-2 mt-0.5">
                                <p className="text-[#6b7d6b] text-xs flex items-center gap-1 truncate">
                                  <MapPin className="w-3 h-3" /> {c.lake_name}
                                </p>
                                {token && c.user_id !== user?.id && (
                                  <button
                                    onClick={() => reportCatch(c.id)}
                                    className="text-[#6b7d6b] hover:text-red-400 transition-colors text-[11px] flex items-center gap-1"
                                    data-testid={`report-catch-${c.id}`}
                                    title="Report suspicious entry"
                                  >
                                    <Flag className="w-3 h-3" /> Report
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Private Leagues */}
          <TabsContent value="private">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="btn-gold" onClick={() => setShowCreateLeague(true)} data-testid="create-league-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Private League
                </Button>
                <Button className="btn-ghost" onClick={() => setShowJoinLeague(true)} data-testid="join-private-league-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Join with Code
                </Button>
              </div>
              {privateLeagues.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Lock className="w-16 h-16 mx-auto mb-4 text-[#4a504a]" />
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">No Private Leagues Yet</h3>
                  <p className="text-[#6b7d6b] max-w-md mx-auto">Create your own league and invite friends, or join an existing one with an invite code.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {privateLeagues.map((league) => (
                    <motion.div key={league.id} className="glass-card p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid={`private-league-${league.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">{league.name}</h4>
                          <p className="text-[#8a948a] text-sm mt-1">{league.description}</p>
                        </div>
                        <Lock className="w-5 h-5 text-[#6b7d6b]" />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-[#6b7d6b] mb-4">
                        <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {league.member_count} members</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${league.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-[#4a504a]/20 text-[#8a948a]'}`}>{league.status}</span>
                      </div>
                      {league.invite_code && (
                        <div className="flex items-center gap-2 p-3 bg-[#141714] rounded-lg mb-4">
                          <span className="text-[#6b7d6b] text-sm">Invite Code:</span>
                          <code className="text-[#c8b273] font-mono font-bold">{league.invite_code}</code>
                          <Button variant="ghost" size="sm" onClick={() => copyInviteCode(league.invite_code)} className="ml-auto">
                            {copiedCode ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      )}
                      <Button className="btn-ghost w-full" onClick={() => refreshLeaderboard(league.id)}>
                        View Leaderboard <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Submit catch modal */}
      {communityLeague && (
        <SubmitCatchModal
          open={showSubmitCatch}
          onClose={() => setShowSubmitCatch(false)}
          leagueId={communityLeague.id}
          token={token}
          bundles={bundles}
          onSuccess={() => {
            refreshLeaderboard(communityLeague.id);
            refreshCatches(communityLeague.id);
            refreshMyStats(communityLeague.id);
          }}
        />
      )}

      {/* Create League */}
      <Dialog open={showCreateLeague} onOpenChange={setShowCreateLeague}>
        <DialogContent className="bg-[#0b0d0b] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
              <Plus className="w-6 h-6 text-[#c8b273]" /> Create Private League
            </DialogTitle>
            <DialogDescription className="text-[#8a948a]">Invite friends with the auto-generated code.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLeague} className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">League Name *</label>
              <Input placeholder="e.g. The Syndicate Challenge"
                value={createLeagueForm.name}
                onChange={(e) => setCreateLeagueForm((p) => ({ ...p, name: e.target.value }))}
                className="bg-[#141714] border-white/10" required data-testid="create-league-name"
              />
            </div>
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Description</label>
              <Textarea placeholder="What's this league about?"
                value={createLeagueForm.description}
                onChange={(e) => setCreateLeagueForm((p) => ({ ...p, description: e.target.value }))}
                className="bg-[#141714] border-white/10" data-testid="create-league-desc"
              />
            </div>
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Max Members (optional)</label>
              <Input type="number" placeholder="Leave empty for unlimited"
                value={createLeagueForm.max_members}
                onChange={(e) => setCreateLeagueForm((p) => ({ ...p, max_members: e.target.value }))}
                className="bg-[#141714] border-white/10" data-testid="create-league-max"
              />
            </div>
            <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="create-league-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create League'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Join League */}
      <Dialog open={showJoinLeague} onOpenChange={setShowJoinLeague}>
        <DialogContent className="bg-[#0b0d0b] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-[#c8b273]" /> Join Private League
            </DialogTitle>
            <DialogDescription className="text-[#8a948a]">Enter the invite code shared by the league creator.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleJoinPrivateLeague} className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Invite Code</label>
              <Input placeholder="Enter invite code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="bg-[#141714] border-white/10 font-mono text-lg tracking-widest text-center"
                maxLength={16}
                data-testid="join-league-code"
              />
            </div>
            <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="join-league-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join League'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* My Stats */}
      <Dialog open={showStats} onOpenChange={setShowStats}>
        <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-[#c8b273]" /> Your Stats
            </DialogTitle>
            <DialogDescription className="text-[#8a948a]">Points breakdown for the current season.</DialogDescription>
          </DialogHeader>
          {myStats && (
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 bg-[#141714] rounded-lg text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6b7d6b]">Rank</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{myStats.rank ? `#${myStats.rank}` : '—'}</p>
                </div>
                <div className="p-3 bg-[#141714] rounded-lg text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6b7d6b]">Points</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{myStats.total_points}</p>
                </div>
                <div className="p-3 bg-[#141714] rounded-lg text-center">
                  <p className="text-[10px] uppercase tracking-wider text-[#6b7d6b]">Catches</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{myStats.catch_count}</p>
                </div>
              </div>
              <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold pt-2">Points by Category</p>
              {Object.keys(myStats.points_by_category || {}).length === 0 ? (
                <p className="text-[#6b7d6b] text-sm text-center py-4">Submit a catch to start earning points.</p>
              ) : (
                <ul className="space-y-2">
                  {Object.entries(myStats.points_by_category).map(([k, v]) => (
                    <li key={k} className="flex justify-between p-2.5 bg-[#141714] rounded-lg" data-testid={`my-stats-cat-${k}`}>
                      <span className="text-[#e2e4e2] text-sm">{CATEGORY_LABELS[k] || k}</span>
                      <span className="font-['Barlow_Condensed'] font-bold text-[#c8b273]">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
              {myStats.species_caught?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold pt-2">Species caught</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {myStats.species_caught.map((s) => (
                      <span key={s} className="px-2 py-1 bg-[#141714] rounded text-xs text-[#e2e4e2]">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Points Info */}
      <Dialog open={showPointsInfo} onOpenChange={setShowPointsInfo}>
        <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-[#c8b273]" /> How Points Work
            </DialogTitle>
            <DialogDescription className="text-[#8a948a]">
              Skill, consistency &amp; engagement — not just the biggest fish.
            </DialogDescription>
          </DialogHeader>
          {pointsConfig && (
            <div className="space-y-4 mt-4 text-sm">
              <Section title="Base by species">
                {Object.entries(pointsConfig.species_base).map(([s, p]) => (
                  <Row key={s} label={s} pts={`+${p}`} testid={`pts-species-${s}`} />
                ))}
              </Section>
              <Section title="Carp variant bonus (small)">
                {Object.entries(pointsConfig.carp_variant_bonus).map(([s, p]) => (
                  <Row key={s} label={s} pts={p === 0 ? '—' : `+${p}`} />
                ))}
              </Section>
              <Section title="Weight tiers (non-stacking)">
                {pointsConfig.weight_tiers.map((t) => (
                  <Row key={t.threshold} label={t.label} pts={`+${t.points}`} />
                ))}
              </Section>
              <Section title="Activity bonuses">
                <Row label="First fish of the week" pts={`+${pointsConfig.activity_bonuses.first_fish_of_week}`} />
                <Row label="Night catch (8pm–6am)" pts={`+${pointsConfig.activity_bonuses.night_catch}`} />
                <Row label="New lake unlocked" pts={`+${pointsConfig.activity_bonuses.new_lake}`} />
                <Row label="Back-to-back catch (<12h)" pts={`+${pointsConfig.activity_bonuses.back_to_back}`} />
              </Section>
              <Section title="Engagement bonuses">
                <Row label="Used a Carp Cast bundle" pts={`+${pointsConfig.engagement_bonuses.used_bundle}`} />
                <Row label="Full session details (bait + duration + weather)" pts={`+${pointsConfig.engagement_bonuses.full_session}`} />
                <Row label="Photo evidence (mandatory)" pts={`+${pointsConfig.engagement_bonuses.photo}`} />
              </Section>
              <div className="p-3 bg-[#c8b273]/10 border border-[#c8b273]/30 rounded-lg flex gap-2">
                <AlertTriangle className="w-5 h-5 text-[#c8b273] shrink-0 mt-0.5" />
                <p className="text-[#c8b273] text-xs">
                  Photo evidence is required on every catch. Suspicious entries can be reported anonymously — 3 reports auto-hides until admin review.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold mb-1.5">{title}</p>
    <ul className="space-y-1.5">{children}</ul>
  </div>
);

const Row = ({ label, pts, testid }) => (
  <li className="flex justify-between items-center p-2 bg-[#141714] rounded" data-testid={testid}>
    <span className="text-[#e2e4e2]">{label}</span>
    <span className="font-['Barlow_Condensed'] font-bold text-[#c8b273]">{pts}</span>
  </li>
);

export default LeaguePage;
